declare global {
    interface Window {
        historyIndex: {
            current: number;
            restore: (savedIndex: number) => void;
        };
    }
}

/**
 * This function patches the browser history methods to keep track of the "current index" in the history state.
 * This makes it possible to save and restore the history to arbitrary points.
 *
 * This was initially added so that closing the modals would restore the history go back enough steps in history
 * to get to the point where the modal was opened.
 *
 * @example
 * ```
 * const savedIndex = window.historyIndex.current;
 * // ...much later, in a different part of the codebase
 * // restore the browser history exactly to the point where the initial index was saved
 * window.historyIndex.restore(savedIndex);
 * ```
 *
 * Cross-origin iframes (e.g. third-party widgets) inject their own entries into the joint session history.
 * Those entries do not go through our patched `pushState`, so a `history.go(delta)` computed from our own
 * counter would undershoot. When the Navigation API is available we therefore track position via
 * `navigation.entries()` (which is per-frame and excludes iframe entries) and restore with `traverseTo`,
 * which jumps straight to the saved entry by key regardless of how many iframe entries sit in between.
 *
 * CAVEATS:
 *  This disallows pushing non-object states to the history, so custom code that does that will throw.
 *  I think this is an acceptable compromise as they can always wrap their state in an object.
 */
export function patchHistoryWithIndex() {
    const nav = window.navigation;

    let index = 0;

    const originalPush = history.pushState;
    const originalReplace = history.replaceState;

    const onPopState = (e: PopStateEvent) => {
        if (e.state && typeof e.state === "object" && "__histIdx" in e.state) {
            index = (e.state as { __histIdx: number }).__histIdx;
        }
    };

    history.pushState = function (state, title, url) {
        validateState(state);
        index++;
        return originalPush.call(this, { __histIdx: index, ...(typeof state === "object" ? state : {}) }, title, url);
    };

    history.replaceState = function (state, title, url) {
        validateState(state);
        return originalReplace.call(
            this,
            { __histIdx: index, ...(typeof state === "object" ? state : {}) },
            title,
            url,
        );
    };

    window.addEventListener("popstate", onPopState);

    window.historyIndex = {
        get current() {
            return nav?.currentEntry?.index ?? index;
        },
        restore(savedIndex) {
            if (nav) {
                const target = nav.entries()[savedIndex];
                if (target && target.key !== nav.currentEntry?.key) {
                    nav.traverseTo(target.key).committed?.catch(() => {
                        // The entry may no longer be traversable (e.g. pruned); nothing to restore to.
                    });
                }
                return;
            }
            const delta = savedIndex - index;
            if (delta !== 0) history.go(delta);
        },
    };
    return () => {
        history.pushState = originalPush;
        history.replaceState = originalReplace;
        window.removeEventListener("popstate", onPopState);
    };
}
function validateState(state: unknown) {
    if (state && typeof state !== "object") {
        throw new Error("Pushing a non-object state is not supported. Please wrap it in an object.");
    }
}
